import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import auditService from '../../services/auditService';
import FiltroPillole from '../common/FiltroPillole';
import BarraElenco from '../common/BarraElenco';
import { CONTENITORE_TABELLA, TABELLA_PAGINA, TESTATA_TABELLA } from '../common/stiliTabella';

/**
 * L'etichetta italiana di un evento arriva dal backend (`etichettaTipo`), che è la sorgente
 * unica per la tabella e per il CSV esportato: mantenerne una copia qui faceva divergere i
 * due. Se il campo mancasse — un tipo che il backend non etichetta — si ripiega sul nome
 * tecnico, perché una riga deve restare leggibile invece di sparire a metà.
 */
const etichetta = (evento) => (evento && evento.etichettaTipo) || (evento && evento.tipo) || '';

const CATEGORIE = [
    { valore: null, etichetta: 'Tutte' },
    { valore: 'RUOLI', etichetta: 'Ruoli' },
    { valore: 'ASSEGNAZIONI', etichetta: 'Assegnazioni' },
    { valore: 'UTENTI', etichetta: 'Utenti' },
    { valore: 'GRUPPI', etichetta: 'Gruppi' },
    { valore: 'BREAK_GLASS', etichetta: 'Break-glass' },
];

// La categoria arriva dal backend (AuthAuditCategory): il colore si deriva da lì e non
// dall'etichetta italiana, che è testo da mostrare e non una chiave.
//
// Il ripiego non è difensivismo: `AuthAuditCategory` è un enum e una categoria nuova
// compilerebbe senza toccare questo file, uscendo qui come `undefined`. Meglio una
// pastiglia neutra che una senza stile.
const STILE_CATEGORIA = {
    RUOLI: { color: 'primary', variant: 'outlined' },
    ASSEGNAZIONI: { color: 'primary', variant: 'outlined' },
    UTENTI: { color: 'default', variant: 'outlined' },
    GRUPPI: { color: 'primary', variant: 'outlined' },
    BREAK_GLASS: { color: 'warning', variant: 'filled' },
};
const STILE_DI_RIPIEGO = { color: 'default', variant: 'outlined' };

const DIMENSIONE_PAGINA_DEFAULT = 50;

// Il tetto imposto dal backend (AuditController.DIMENSIONE_MASSIMA): sopra questo numero
// una pagina non si concede.
const DIMENSIONE_MASSIMA = 200;

const OPZIONI_DIMENSIONE = [10, 25, 50, 100, 200];

const RICERCA_DEBOUNCE_MS = 400;

/**
 * L'intervallo scelto (una data `AAAA-MM-GG` da un input `type="date"`) diventa l'istante che
 * i due endpoint accettano come `da` / `a`. `da` è l'inizio del giorno, `a` la sua fine: chi
 * sceglie «al 3 settembre» intende includere il 3 settembre intero, non fermarsi a
 * mezzanotte. La conversione è nel fuso del browser, lo stesso in cui la colonna «Data e ora»
 * è mostrata, così il filtro corrisponde a ciò che si legge.
 */
const inizioGiorno = (data) => (data ? new Date(`${data}T00:00:00`).toISOString() : undefined);

const fineGiorno = (data) => (data ? new Date(`${data}T23:59:59.999`).toISOString() : undefined);

/**
 * Ricava il nome del file dall'header Content-Disposition che AuditController costruisce
 * (`audit-2026-09-03.csv`): senza questo il download perderebbe la data e due esportazioni
 * in giorni diversi si sovrascriverebbero come `audit.csv` / `audit (1).csv`.
 */
const nomeFileDaHeader = (headers) => {
    const intestazione = headers && (headers['content-disposition'] || headers['Content-Disposition']);
    if (!intestazione) {
        return 'audit.csv';
    }
    const corrispondenza = /filename="?([^";]+)"?/i.exec(intestazione);
    return (corrispondenza && corrispondenza[1]) || 'audit.csv';
};

const formattaDataOra = (isoString) => {
    if (!isoString) {
        return '';
    }
    return new Date(isoString).toLocaleString('it-IT');
};

const formattaData = (isoString) => {
    if (!isoString) {
        return '';
    }
    return new Date(isoString).toLocaleDateString('it-IT');
};

/**
 * Legge il messaggio di errore di un'esportazione rifiutata (422): il corpo arriva come
 * blob perche' la richiesta usa responseType 'blob' (§10.5, D3), quindi va letto e
 * riconvertito in JSON prima di poterne leggere `conteggio` e `messaggio`.
 */
const leggiErroreEsportazione = async (error) => {
    const data = error && error.response && error.response.data;
    if (!data) {
        return null;
    }
    try {
        const testo = typeof data.text === 'function' ? await data.text() : data;
        const corpo = typeof testo === 'string' ? JSON.parse(testo) : testo;
        return corpo;
    } catch (parseError) {
        return null;
    }
};

/**
 * Tab «Audit» della pagina «Ruoli e permessi» (blocco 9, spec §10.5).
 *
 * La colonna «Oggetto» rende il campo `oggetto` cosi' come arriva dal backend, gia' calcolato
 * da `AuthAuditQueryService` (R1): non lo ricalcola da `roleId` o `permissionCode`, altrimenti
 * tabella ed export CSV divergerebbero. Lo stesso vale per la colonna «Azione», che rende
 * `etichettaTipo` (R5).
 *
 * L'intervallo «Da» / «A» valorizza i parametri `da` e `a` che entrambi gli endpoint già
 * accettano, e vale per la vista come per l'esportazione: senza, il messaggio del 422
 * indicherebbe una leva che l'interfaccia non offre, e su un'installazione di lungo corso
 * l'esportazione diventerebbe irraggiungibile per sempre (non esiste retention, D2).
 *
 * L'intestazione con la finestra temporale (D2) mostra la data reale del primo evento
 * (`eventoPiuVecchio`): non esiste retention, nulla cancella dal registro. Se il registro e'
 * vuoto quel campo e' `null` e non si mostra alcuna finestra inventata.
 *
 * Il caricamento si dichiara, non si mima: una tabella col solo header e «0 di 0» e'
 * indistinguibile da un elenco vuoto, la stessa regola che UsersTab applica gia'.
 */
export default function AuditTab() {
    const [events, setEvents] = useState([]);
    const [totale, setTotale] = useState(0);
    const [eventoPiuVecchio, setEventoPiuVecchio] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [categoria, setCategoria] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [q, setQ] = useState('');
    const [pagina, setPagina] = useState(0);
    const [dimensione, setDimensione] = useState(DIMENSIONE_PAGINA_DEFAULT);
    const [da, setDa] = useState('');
    const [a, setA] = useState('');
    const [exportError, setExportError] = useState(null);
    const [exporting, setExporting] = useState(false);

    const debounceRef = useRef(null);

    // Token di generazione: ogni chiamata porta il proprio numero, e una risposta si scrive
    // sullo stato solo se e' ancora quella corrente. Senza questo, due click ravvicinati (es.
    // «Ruoli» poi subito «Tutte») possono rispondere fuori ordine e far vincere la risposta
    // obsoleta — l'utente vedrebbe un filtro non corrispondente a cio' che ha selezionato per
    // ultimo, e in un registro di audit la lettura sarebbe «questi eventi non esistono».
    const generazioneRef = useRef(0);

    const load = useCallback(() => {
        const generazione = ++generazioneRef.current;
        setLoading(true);
        setError(false);
        auditService
            .listEvents({
                categorie: categoria ? [categoria] : undefined,
                q: q || undefined,
                da: inizioGiorno(da),
                a: fineGiorno(a),
                pagina,
                dimensione,
            })
            .then((data) => {
                if (generazioneRef.current !== generazione) {
                    return;
                }
                setEvents((data && data.righe) || []);
                setTotale((data && data.totale) || 0);
                setEventoPiuVecchio((data && data.eventoPiuVecchio) || null);
                setLoading(false);
            })
            .catch(() => {
                if (generazioneRef.current !== generazione) {
                    return;
                }
                setError(true);
                setLoading(false);
            });
    }, [categoria, q, da, a, pagina, dimensione]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(
        () => () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        },
        [],
    );

    const handleSelectCategoria = (valore) => {
        setCategoria((corrente) => (corrente === valore ? corrente : valore));
        setPagina(0);
    };

    const handleRicerca = (valore) => {
        setSearchInput(valore);
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            setQ(valore);
            // Un cambio di ricerca torna a pagina 0: restare su una pagina alta col nuovo
            // filtro mostrerebbe una pagina vuota che sembra un elenco vuoto.
            setPagina(0);
        }, RICERCA_DEBOUNCE_MS);
    };

    // Un cambio di intervallo passa dallo stesso `load`, quindi dallo stesso token di
    // generazione dei filtri e della ricerca, e riporta a pagina 0: restare su una pagina alta
    // con un intervallo più stretto mostrerebbe una pagina vuota che sembra un elenco vuoto.
    const handleChangeDa = (event) => {
        setDa(event.target.value);
        setPagina(0);
    };

    const handleChangeA = (event) => {
        setA(event.target.value);
        setPagina(0);
    };

    const handleChangePagina = (event, nuovaPagina) => {
        setPagina(nuovaPagina);
    };

    const handleChangeDimensione = (event) => {
        const nuovaDimensione = Math.min(parseInt(event.target.value, 10), DIMENSIONE_MASSIMA);
        setDimensione(nuovaDimensione);
        setPagina(0);
    };

    const handleExport = () => {
        setExportError(null);
        setExporting(true);
        auditService
            .exportEvents({
                categorie: categoria ? [categoria] : undefined,
                q: q || undefined,
                da: inizioGiorno(da),
                a: fineGiorno(a),
            })
            .then((response) => {
                setExporting(false);
                const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = nomeFileDaHeader(response.headers);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch(async (err) => {
                setExporting(false);
                if (err && err.response && err.response.status === 422) {
                    const corpo = await leggiErroreEsportazione(err);
                    const conteggio = corpo && corpo.conteggio;
                    setExportError(
                        conteggio
                            ? `Ci sono troppe righe da esportare (${conteggio}): restringi la finestra temporale o i filtri.`
                            : 'Ci sono troppe righe da esportare: restringi la finestra temporale o i filtri.',
                    );
                    return;
                }
                setExportError('Non e\' stato possibile esportare il registro.');
            });
    };

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={load}>
                            Riprova
                        </Button>
                    }
                >
                    Non è stato possibile caricare il registro di audit.
                </Alert>
            </Box>
        );
    }

    const conteggio = loading ? (
        'Caricamento…'
    ) : (
        <>
            {`${totale} ${totale === 1 ? 'voce' : 'voci'}`}
            {eventoPiuVecchio ? ` dal ${formattaData(eventoPiuVecchio)}` : ''}
        </>
    );

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ogni modifica ai ruoli, ai permessi e alle assegnazioni resta registrata qui. Le
                voci non si possono cancellare.
            </Typography>

            <BarraElenco
                ricerca={searchInput}
                onRicerca={handleRicerca}
                placeholderRicerca="Cerca"
                filtri={(
                    <FiltroPillole
                        opzioni={CATEGORIE}
                        selezione={categoria}
                        onCambia={handleSelectCategoria}
                        ariaLabel="Filtra per categoria"
                    />
                )}
                conteggio={conteggio}
                azione={(
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExport}
                        disabled={exporting}
                    >
                        Esporta CSV
                    </Button>
                )}
            />

            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    type="date"
                    label="Da"
                    value={da}
                    onChange={handleChangeDa}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'aria-label': 'Da' }}
                    sx={{ width: 170 }}
                />
                <TextField
                    size="small"
                    type="date"
                    label="A"
                    value={a}
                    onChange={handleChangeA}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'aria-label': 'A' }}
                    sx={{ width: 170 }}
                />
            </Box>

            {exportError && (
                <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
                    {exportError}
                </Alert>
            )}

            <TableContainer {...CONTENITORE_TABELLA}>
                <Table size="small" sx={TABELLA_PAGINA}>
                    <TableHead>
                        <TableRow sx={TESTATA_TABELLA}>
                            <TableCell>Data e ora</TableCell>
                            <TableCell>Autore</TableCell>
                            <TableCell>Azione</TableCell>
                            <TableCell>Oggetto</TableCell>
                            <TableCell>Dettaglio</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={5}>
                                    <Typography variant="body2" color="text.secondary">
                                        Caricamento…
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && events.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5}>
                                    <Typography variant="body2" color="text.secondary">
                                        Nessun evento da mostrare.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading &&
                            events.map((event) => (
                                <TableRow key={event.id}>
                                    <TableCell>{formattaDataOra(event.occorsoIl)}</TableCell>
                                    <TableCell>{event.attore}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={etichetta(event)}
                                            size="small"
                                            data-testid={`azione-${event.tipo}`}
                                            {...(STILE_CATEGORIA[event.categoria] || STILE_DI_RIPIEGO)}
                                        />
                                    </TableCell>
                                    <TableCell>{event.oggetto}</TableCell>
                                    <TableCell>{event.dettaglio}</TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totale}
                    page={pagina}
                    onPageChange={handleChangePagina}
                    rowsPerPage={dimensione}
                    onRowsPerPageChange={handleChangeDimensione}
                    rowsPerPageOptions={OPZIONI_DIMENSIONE}
                    labelRowsPerPage="Righe per pagina"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
                />
            </TableContainer>
        </Box>
    );
}
